<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button
                variant="link"
                class="list-view-icon-btn"
                :class="{ 'list-view-icon-btn--active': filtersOpen }"
                v-b-tooltip.hover.bottom
                :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                @click="filtersOpen = !filtersOpen"
              >
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 220px;">
              <label class="filter-label">Lista de precios</label>
              <v-select :options="priceListOptions" v-model="filters.priceList" placeholder="Todas las activas" :select-on-tab="true" :disabled="loading || applying || selectingAll" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Buscar producto</label>
              <b-form-input size="sm" v-model="filters.search" placeholder="Nombre, código o código de barras" :disabled="loading || applying || selectingAll" @keyup.enter="search" />
            </div>
            <div class="list-view-filter" style="width: 110px;">
              <label class="filter-label">Precio mínimo</label>
              <b-form-input size="sm" type="number" min="0" v-model.number="filters.priceMin" :disabled="loading || applying || selectingAll" @keyup.enter="search" />
            </div>
            <div class="list-view-filter" style="width: 110px;">
              <label class="filter-label">Precio máximo</label>
              <b-form-input size="sm" type="number" min="0" v-model.number="filters.priceMax" :disabled="loading || applying || selectingAll" @keyup.enter="search" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 320px;">
              <label class="filter-label">Regla de revisión</label>
              <v-select
                :options="reviewRuleOptions"
                v-model="filters.dirtyRule"
                :select-on-tab="true"
                :clearable="false"
                :disabled="loading || applying || selectingAll"
              />
            </div>
            <b-button variant="outline-dark" size="sm" :disabled="loading || applying || selectingAll" @click="search" v-b-tooltip.hover title="Buscar precios" aria-label="Buscar precios" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="search" />
            </b-button>
          </div>
          <div v-if="filters.dirtyRule" class="price-audit-guidance">
            <span class="price-audit-guidance__item"><AppIcon name="info-circle" /><span>{{ reviewRuleHint }}</span></span>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12">
          <b-card>
            <div v-if="!highPrecisionPricing" class="app-alert app-alert--warning mb-3" role="status">
              <span class="app-alert__icon"><AppIcon name="info" /></span>
              <div class="app-alert__content small">Algunos precios finales pueden omitirse al guardar. Para evitarlo, activá el parámetro del sistema “Conservar precisión en precios de venta” en el módulo Costos.</div>
            </div>
            <div v-if="lastSkipped.length" class="price-audit-skipped mb-3" role="status">
              <div class="price-audit-skipped__heading">
                <strong>{{ lastSkipped.length }} precio{{ lastSkipped.length === 1 ? '' : 's' }} omitido{{ lastSkipped.length === 1 ? '' : 's' }}</strong>
                <b-button variant="link" size="sm" @click="lastSkipped = []">Cerrar</b-button>
              </div>
              <div class="price-audit-skipped__list">
                <div v-for="row in lastSkipped" :key="row.id">{{ row.product_name }} <span v-if="row.product_code">({{ row.product_code }})</span> · {{ row.price_list_name }} · ID {{ row.id }}</div>
              </div>
            </div>
            <b-row v-if="rows.length">
              <b-col sm="12" class="mb-2 d-flex align-items-center flex-wrap price-audit-select-bar">
                <span v-if="selectingAll" class="price-audit-selecting" role="status" aria-live="polite"><b-spinner small /> Seleccionando resultados…</span>
                <b-dropdown v-else-if="!selected.length" text="Seleccionar" size="sm" variant="outline-dark">
                  <b-dropdown-item @click="selectPage()">
                    Esta página ({{ rows.length }})
                  </b-dropdown-item>
                  <b-dropdown-item @click="selectAllMatching()">
                    Todos los resultados del filtro ({{ pagination.total }})
                  </b-dropdown-item>
                </b-dropdown>
                <div v-else class="price-audit-selection-actions">
                  <b-button variant="outline-secondary" size="sm" @click="clearSelection()">
                    <AppIcon name="close" class="mr-1" />Borrar selección ({{ selected.length }})
                  </b-button>
                  <b-button v-if="pageUnselectedCount" variant="outline-dark" size="sm" @click="selectPage()">
                    Agregar esta página ({{ pageUnselectedCount }})
                  </b-button>
                </div>
                <div v-if="selected.length" class="price-audit-bulk-actions">
                  <span class="price-audit-bulk-actions__count">redondear a:</span>
                  <b-button variant="dark" size="sm" @click="openBulkModal(1, 'Sin decimales')">Sin decimales</b-button>
                  <b-button variant="dark" size="sm" @click="openBulkModal(5, 'Múltiplo de 5')">Múltiplo de 5</b-button>
                  <b-button variant="dark" size="sm" @click="openBulkModal(10, 'Múltiplo de 10')">Múltiplo de 10</b-button>
                  <b-button variant="dark" size="sm" @click="openBulkModal(100, 'Múltiplo de 100')">Múltiplo de 100</b-button>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="12">
                <b-table
                  class="app-table"
                  :items="rows"
                  :fields="fields"
                  sticky-header="60vh"
                  small
                  :stacked="isMobileViewport ? 'md' : false"
                  v-if="rows.length"
                >
                  <template v-slot:cell(select_multi)="data">
                    <button
                      type="button"
                      class="price-audit-row-select"
                      :class="{ 'price-audit-row-select--selected': isSelected(data.item) }"
                      :aria-label="isSelected(data.item) ? 'Quitar producto de la selección' : 'Seleccionar producto'"
                      :aria-pressed="isSelected(data.item) ? 'true' : 'false'"
                      :disabled="selectingAll"
                      @click.stop="toggleSelect(data.item)"
                    >
                      <AppIcon :name="isSelected(data.item) ? 'check2-square' : 'square'"
                               class="price-audit-row-select__icon"
                               font-scale="1.3" />
                      <span class="price-audit-row-select__label">{{ isSelected(data.item) ? 'Seleccionado' : 'Seleccionar' }}</span>
                    </button>
                  </template>
                  <template v-slot:cell(product_name)="data">
                    <div class="price-audit-product-name">{{ data.item.product_name }}</div>
                    <small class="price-audit-product-code">{{ data.item.product_code }}</small>
                    <small v-if="data.item.purchase_unit_name || data.item.sale_unit_name" class="price-audit-units">Compra: {{ data.item.purchase_unit_name || '—' }} · Venta: {{ data.item.sale_unit_name || '—' }}</small>
                  </template>
                  <template v-slot:cell(price_list_name)="data">
                    <b-badge pill variant="light" class="price-audit-list-badge">
                      {{ data.item.price_list_name }}
                    </b-badge>
                  </template>
                  <template v-slot:cell(price)="data">
                    <div class="price-audit-price-cell">
                      <strong class="price-audit-price-cell__value">{{ fPrice(data.item.price) }}</strong>
                      <small v-if="data.item.sale_unit_name" class="price-audit-price-cell__unit">por {{ data.item.sale_unit_name }}</small>
                    </div>
                  </template>
                  <template v-slot:cell(utility)="data">
                    <div class="price-audit-utility-cell">
                      <b-badge pill variant="primary" class="price-audit-utility-badge">
                        {{ fUtility(effectiveUtilityOf(data.item)) }}%
                      </b-badge>
                      <small v-if="isUsingListDefault(data.item)" class="price-audit-utility-cell__source">Default lista</small>
                    </div>
                  </template>
                </b-table>

                <div v-if="loading" class="text-center text-muted py-4" role="status">
                  <b-spinner small class="mr-2" />Calculando precios actuales…
                </div>

                <EmptyState
                  v-else-if="!rows.length"
                  icon="price_check"
                  title="No se encontraron precios con esos filtros"
                  description="Ajustá los filtros de arriba y volvé a buscar." />

                <div v-if="rows.length" class="app-table-footer">
                  <span class="list-view-toolbar__count">
                    {{ pagination.total }} resultado{{ pagination.total == 1 ? '' : 's' }}
                  </span>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="12">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="pagination.total">
                <b-pagination
                  class="mb-0"
                  size="sm"
                  pills
                  :total-rows="pagination.total"
                  :per-page="pagination.per_page"
                  :disabled="loading || applying || selectingAll"
                  v-model="pagination.current_page"
                  @input="search({ keepSelection: true })" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-col>

    <b-modal
      v-model="bulkConfirm.show"
      size="md"
      hide-footer
      :no-close-on-backdrop="applying"
      :no-close-on-esc="applying"
      :hide-header-close="applying"
    >
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="save" /></span>
        <h5 class="app-modal-header__title">Guardar ajustes de precios</h5>
      </div>
      <p>
        ¿Confirmás guardar el ajuste de {{ bulkModalValidCount }} producto{{ bulkModalValidCount == 1 ? '' : 's' }}?
      </p>
      <p class="text-muted small">
        El sistema recalculará la utilidad exacta de cada producto para conservar el precio final seleccionado.
      </p>
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-2" :disabled="applying" @click="bulkConfirm.show = false">Cancelar</b-button>
        <b-button variant="dark" :disabled="applying" @click="confirmBulk">
          <b-spinner small v-if="applying" class="mr-1" />
          <AppIcon v-else name="save" class="mr-1" />
          {{ applying ? 'Guardando...' : 'Confirmar y guardar' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Previsualización del ajuste en lote antes de guardar -->
    <b-modal
      v-model="bulkModal.show"
      :title="'Aplicar: ' + bulkModal.label"
      size="lg"
      ok-title="Confirmar y guardar"
      :ok-disabled="applying || !bulkModalValidCount"
      :cancel-disabled="applying"
      :hide-header-close="applying"
      :no-close-on-backdrop="applying"
      :no-close-on-esc="applying"
      cancel-title="Cancelar"
      @ok.prevent="openBulkConfirmation"
    >
      <template v-slot:modal-ok>
        <b-spinner v-if="applying" small class="mr-1" />
        {{ applying ? 'Guardando ajustes…' : 'Confirmar y guardar' }}
      </template>

      <b-overlay :show="applying" opacity="0.96" rounded="sm">
        <template v-slot:overlay>
          <div class="price-audit-apply-progress">
            <span class="price-audit-apply-progress__icon"><b-spinner variant="primary" /></span>
            <strong>Guardando ajustes</strong>
            <span>Estamos recalculando la utilidad de {{ bulkModalValidCount }} producto{{ bulkModalValidCount == 1 ? '' : 's' }}.</span>
          </div>
        </template>

        <section :aria-busy="applying ? 'true' : 'false'">
          <div class="price-audit-apply-info">
            <span class="price-audit-apply-info__icon"><AppIcon name="price_check" /></span>
            <div>
              <strong>El precio final define el ajuste</strong>
              <p>Enviamos el importe objetivo de cada producto. El sistema recalcula la utilidad exacta con el costo y el IVA vigentes.</p>
              <span class="price-audit-apply-info__note"><AppIcon name="verified" /> La utilidad de la tabla es una estimación.</span>
            </div>
          </div>

          <div v-if="bulkModalInvalidCount" class="price-audit-bulk-notice price-audit-bulk-notice--warning">
            <AppIcon name="exclamation-triangle" />
            <span>{{ bulkModalInvalidCount }} producto{{ bulkModalInvalidCount == 1 ? '' : 's' }} no se puede{{ bulkModalInvalidCount == 1 ? '' : 'n' }} ajustar automáticamente y se va{{ bulkModalInvalidCount == 1 ? '' : 'n' }} a omitir. Requiere{{ bulkModalInvalidCount == 1 ? '' : 'n' }} una revisión manual.</span>
          </div>
          <div v-if="bulkModalAdjustedCount" class="price-audit-bulk-notice">
            {{ bulkModalAdjustedCount }} precio{{ bulkModalAdjustedCount == 1 ? '' : 's' }} se redondeará{{ bulkModalAdjustedCount == 1 ? '' : 'n' }} hacia arriba porque el múltiplo más cercano no permite guardar una utilidad válida.
          </div>
          <div v-if="bulkModalAdjustedDownCount" class="price-audit-bulk-notice">
            {{ bulkModalAdjustedDownCount }} precio{{ bulkModalAdjustedDownCount == 1 ? '' : 's' }} se redondeará{{ bulkModalAdjustedDownCount == 1 ? '' : 'n' }} hacia abajo porque el múltiplo más cercano supera la utilidad máxima permitida.
          </div>
          <div v-if="bulkModal.preview.length > bulkModalPreviewLimit" class="price-audit-bulk-notice">
            Mostrando los primeros {{ bulkModalPreviewLimit }} de {{ bulkModal.preview.length }} productos seleccionados — la regla se aplica a los {{ bulkModal.preview.length }} igual.
          </div>

          <b-table
            class="app-table"
            small
            :items="bulkModalPreviewVisible"
            :fields="bulkModalFields"
            sticky-header="45vh"
            responsive
            fixed
          >
            <template v-slot:cell(product_name)="data">{{ data.item.row.product_name }}<small v-if="data.item.row.sale_unit_name" class="price-audit-units">Venta por {{ data.item.row.sale_unit_name }}</small></template>
            <template v-slot:cell(oldPrice)="data">{{ fPrice(data.item.oldPrice) }}</template>
            <template v-slot:cell(newPrice)="data"><b>{{ fPrice(data.item.newPrice) }}</b></template>
            <template v-slot:cell(newUtility)="data">
              <span v-if="data.item.invalid" class="text-warning">{{ data.item.invalidReason }}</span>
              <span v-else>≈ {{ fUtility(data.item.newUtility) }}%</span>
            </template>
          </b-table>
        </section>
      </b-overlay>
    </b-modal>

  </b-row>
</template>

<script>
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Params from '@/config/parameters'
  import Helper from '@/handler/helper'
  import Error from '@/handler/error'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import pricingServiceAPI from '@/components/inc/productPricing/services'
  import productsServiceAPI from './services'
  import { effectiveUtility, roundToNearestMultiple } from '@/components/inc/productPricing/pricingMath'

  const MAX_PRODUCT_UTILITY = 9999.9999999999 // m_prices_lists_products.utility: decimal(14,10)

  export default {
    components: {
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.PRODUCTOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'priceAudit',
          elements: {},
        },
        highPrecisionPricing: Helper.hasParametersAccess(Params.P75),
        isMobileViewport: false,
        filtersOpen: false,
        loading: false,
        applying: false,
        priceListOptions: [],
        reviewRuleOptions: [
          { code: 'has_cents', label: 'Con centavos' },
          { code: 'not_multiple_5', label: 'Pendientes de redondear a 5' },
          { code: 'not_multiple_10', label: 'Pendientes de redondear a 10' },
          { code: 'not_multiple_100', label: 'Pendientes de redondear a 100' },
        ],
        filters: {
          priceList: null,
          search: '',
          priceMin: null,
          priceMax: null,
          // La auditoria siempre inicia sobre precios a revisar: no se muestra
          // el catalogo completo sin una regla que lo justifique.
          dirtyRule: { code: 'has_cents', label: 'Con centavos' },
        },
        rows: [],
        pagination: { current_page: 1, last_page: 1, total: 0, per_page: 100 },
        selected: [],
        lastSkipped: [],
        selectingAll: false,
        bulkModal: { show: false, label: '', multiple: 1, preview: [] },
        bulkConfirm: { show: false },
      }
    },
    created() {
      Helper.hasAccessView(this.access)
      this.filtersOpen = StorageLocal.getValue('filters_open_priceAudit') == 'true'
      this.loadPriceLists()
      this.search()
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_priceAudit', value ? 'true' : 'false')
      },
    },
    computed: {
      fields() {
        return [
          { key: 'select_multi', label: '', class: 'align-middle ' + this.alignClass('text-center'), width: '30px' },
          { key: 'product_name', label: 'Producto', class: 'align-middle' },
          { key: 'price_list_name', label: 'Lista', class: 'align-middle' },
          { key: 'price', label: 'Precio final', class: 'align-middle ' + this.alignClass('text-right') },
          { key: 'utility', label: 'Utilidad', class: 'align-middle ' + this.alignClass('text-right') },
        ]
      },
      bulkModalFields() {
        return [
          { key: 'product_name', label: 'Producto', thStyle: { width: '52%' } },
          { key: 'oldPrice', label: 'Precio actual', thStyle: { width: '15%' } },
          { key: 'newPrice', label: 'Precio nuevo', thStyle: { width: '15%' } },
          { key: 'newUtility', label: 'Utilidad estimada', thStyle: { width: '18%' } },
        ]
      },
      reviewRuleHint() {
        const rule = this.filters.dirtyRule ? this.filters.dirtyRule.code : null
        const hints = {
          has_cents: 'Muestra precios con decimales. Después elegís a qué base redondearlos.',
          not_multiple_5: 'Muestra sólo precios que cambiarían al redondear a múltiplos de 5.',
          not_multiple_10: 'Muestra sólo precios que cambiarían al redondear a múltiplos de 10.',
          not_multiple_100: 'Muestra sólo precios que cambiarían al redondear a múltiplos de 100.',
        }
        return hints[rule] || 'Elegí una regla para enfocar la revisión, o dejala vacía para ver todos los precios.'
      },
      pageUnselectedCount() {
        return this.rows.filter(row => !this.isSelected(row)).length
      },
      bulkModalValidCount() {
        return this.bulkModal.preview.filter(preview => !preview.invalid).length
      },
      bulkModalInvalidCount() {
        return this.bulkModal.preview.filter(preview => preview.invalid).length
      },
      bulkModalAdjustedCount() {
        return this.bulkModal.preview.filter(preview => preview.adjustedUp && !preview.invalid).length
      },
      bulkModalAdjustedDownCount() {
        return this.bulkModal.preview.filter(preview => preview.adjustedDown && !preview.invalid).length
      },
      // Con "seleccionar todos" puede haber miles de filas en bulkModal.preview
      // -- renderizar esa tabla completa en el modal la vuelve lenta. La regla
      // igual se aplica a todas (confirmBulk usa bulkModal.preview completo,
      // no este recorte), esto es solo para que el modal no se cuelgue.
      bulkModalPreviewLimit() {
        return 50
      },
      bulkModalPreviewVisible() {
        return this.bulkModal.preview.slice(0, this.bulkModalPreviewLimit)
      },
    },
    methods: {
      updateMobileViewport(event) {
        this.isMobileViewport = event.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      fPrice(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(this.toNumber(value))
      },
      fUtility(value) {
        return Math.round(this.toNumber(value) * 100) / 100
      },
      toNumber(value) {
        const number = parseFloat(value)
        return isNaN(number) ? 0 : number
      },
      isNum(value) {
        return value !== null && value !== undefined && value !== '' && !isNaN(value)
      },
      effectiveUtilityOf(row) {
        return effectiveUtility({ utility: row.utility, defaultUtility: row.price_list_default_utility })
      },
      isUsingListDefault(row) {
        return this.toNumber(row.utility) <= 0 && this.toNumber(row.price_list_default_utility) > 0
      },
      loadPriceLists() {
        productsServiceAPI.obtenerListaPreciosSimple()
          .then(response => {
            this.priceListOptions = (response.data || []).map(item => ({ code: item.id, label: item.name }))
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
      },
      // Payload compartido por la busqueda paginada y por "seleccionar todos
      // los resultados" (selectAllMatching) -- los mismos filtros, para que
      // "todos los resultados" sea siempre exactamente lo que se ve filtrado.
      buildFilterPayload() {
        return {
          price_list_id: this.filters.priceList ? this.filters.priceList.code : null,
          search: this.filters.search || null,
          price_min: this.isNum(this.filters.priceMin) ? this.filters.priceMin : null,
          price_max: this.isNum(this.filters.priceMax) ? this.filters.priceMax : null,
          review_rule: this.filters.dirtyRule ? this.filters.dirtyRule.code : null,
        }
      },
      // Al paginar se conserva la selección; una búsqueda nueva la descarta
      // porque los filtros pueden haber cambiado.
      search(options) {
        if(this.loading || this.selectingAll) return Promise.resolve()
        const keepSelection = !!options && options.keepSelection === true
        this.loading = true
        this.rows = []
        if(!keepSelection) this.selected = []
        const payload = {
          ...this.buildFilterPayload(),
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
        }
        return pricingServiceAPI.auditarPrecios(payload)
          .then(response => {
            const data = response.data || {}
            this.rows = data.data || []
            this.pagination = {
              current_page: data.current_page || 1,
              last_page: data.last_page || 1,
              total: data.total || 0,
              per_page: data.per_page || this.pagination.per_page,
            }
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
          .finally(() => { this.loading = false })
      },
      isSelected(item) {
        return this.selected.some(row => row.id === item.id)
      },
      toggleSelect(item) {
        if(this.selectingAll) return
        const index = this.selected.findIndex(row => row.id === item.id)
        if(index === -1) {
          this.selected.push(item)
        } else {
          this.selected.splice(index, 1)
        }
      },
      selectPage() {
        if(this.selectingAll) return
        const newOnes = this.rows.filter(row => !this.isSelected(row))
        this.selected = this.selected.concat(newOnes)
      },
      // Trae los resultados que matchean el filtro actual (no solo la pagina
      // visible) para aplicarles una regla en lote. Si supera el tope seguro,
      // el backend devuelve los primeros 3.000 y avisa que la seleccion quedo
      // acotada, sin perder la posibilidad de trabajar con ese lote.
      selectAllMatching() {
        if(this.selectingAll || this.loading || this.applying) return
        this.selectingAll = true
        const payload = { ...this.buildFilterPayload(), all_matching: true }
        return pricingServiceAPI.auditarPrecios(payload)
          .then(response => {
            const data = response.data || {}
            this.selected = data.data || []
            if(data.limited) {
              this.$awn.success(`Se seleccionaron los primeros ${data.selected_total} de ${data.total} resultados.`)
            }
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
          .finally(() => { this.selectingAll = false })
      },
      clearSelection() {
        this.selected = []
      },
      // Estimacion SOLO para la previsualizacion (no se manda al backend):
      // deriva la utilidad sin pedir costo/descuento/IVA por separado,
      // reconstruyendo la base desde el precio vivo sin redondear. El ajuste
      // real envía el precio objetivo y el backend despeja con los datos
      // vigentes (PriceListProductsController::priceAuditApply).
      computeNewUtility(row, newPrice) {
        const oldUtility = this.effectiveUtilityOf(row)
        const oldPrice = this.isNum(row.raw_price) ? this.toNumber(row.raw_price) : this.toNumber(row.price)
        const ivaFactorEquivalent = 1 + oldUtility / 100
        if(!(oldPrice > 0) || !(ivaFactorEquivalent > 0)) return null
        const k = oldPrice / ivaFactorEquivalent
        if(!(k > 0)) return null
        return (this.toNumber(newPrice) / k - 1) * 100
      },
      validRoundedPrice(row, newPrice, newUtility) {
        return newPrice > 0 && newUtility !== null && newUtility >= 0 &&
          newUtility <= MAX_PRODUCT_UTILITY &&
          !(newUtility === 0 && this.toNumber(row.price_list_default_utility) > 0)
      },
      openBulkModal(multiple, label) {
        this.bulkModal.multiple = multiple
        this.bulkModal.label = label
        this.bulkModal.preview = this.selected.map(row => {
          const nearest = roundToNearestMultiple(row.price, multiple)
          const lower = Math.floor(this.toNumber(row.price) / multiple) * multiple
          const upper = Math.ceil(this.toNumber(row.price) / multiple) * multiple
          // Si el más cercano queda fuera del rango de utilidad, probar el
          // múltiplo vecino. Esto cubre tanto utilidad negativa como máxima.
          const candidates = [...new Set([nearest, lower, upper, nearest - multiple, nearest + multiple])]
          const options = candidates.map(price => ({ price, utility: this.computeNewUtility(row, price) }))
          const chosen = options.find(option => this.validRoundedPrice(row, option.price, option.utility)) || options[0]
          const newPrice = chosen.price
          const newUtility = chosen.utility
          const invalid = !this.validRoundedPrice(row, newPrice, newUtility)
          return {
            row,
            oldPrice: row.price,
            newPrice,
            newUtility,
            adjustedUp: !invalid && newPrice > nearest,
            adjustedDown: !invalid && newPrice < nearest,
            invalid,
            invalidReason: newUtility === null
              ? 'Sin base calculable'
              : newUtility > MAX_PRODUCT_UTILITY ? 'Supera la utilidad máxima'
                : newUtility === 0 && this.toNumber(row.price_list_default_utility) > 0 ? 'La utilidad base impide este precio'
                  : 'Requiere utilidad negativa',
          }
        })
        this.bulkModal.show = true
      },
      openBulkConfirmation() {
        if(this.applying || !this.bulkModalValidCount) return
        this.bulkConfirm.show = true
      },
      confirmBulk() {
        // Se manda el precio objetivo, no la utilidad estimada de la
        // previsualizacion: el backend calcula la utilidad exacta con el
        // costo y el IVA reales (ver comentario de computeNewUtility).
        const items = this.bulkModal.preview
          .filter(preview => !preview.invalid)
          .map(preview => ({ id: preview.row.id, target_price: preview.newPrice }))

        if(!items.length) {
          this.$awn.alert('No hay productos válidos para ajustar')
          return
        }

        // Con "seleccionar todos" puede haber hasta 3.000 filas. El backend
        // ya precarga costos y relaciones por tanda; usar 1.000 reduce los
        // viajes HTTP de 15 a 3 sin armar requests excesivamente grandes.
        const chunkSize = 1000
        const chunks = []
        for(let i = 0; i < items.length; i += chunkSize) {
          chunks.push(items.slice(i, i + chunkSize))
        }

        this.applying = true
        this.bulkConfirm.show = false
        chunks.reduce((promise, chunk) => promise.then(result => pricingServiceAPI.aplicarAjustePrecios({ items: chunk }).then(response => {
          const data = response.data || {}
          return {
            updated: result.updated + (Number(data.updated) || 0),
            skipped: result.skipped.concat(Array.isArray(data.skipped) ? data.skipped : []),
          }
        })), Promise.resolve({ updated: 0, skipped: [] }))
          .then(result => {
            if(result.skipped.length) this.$awn.warning(`Se actualizaron ${result.updated} precio(s); ${result.skipped.length} no alcanzaron el importe solicitado.`)
            else this.$awn.success(`Se actualizaron ${result.updated} precio(s)`)
            this.bulkModal.show = false
            const skippedIds = new Set(result.skipped.map(Number))
            const skippedRows = this.bulkModal.preview.filter(preview => skippedIds.has(Number(preview.row.id))).map(preview => preview.row)
            this.lastSkipped = skippedRows
            return this.search().then(() => { this.selected = skippedRows })
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
          .finally(() => { this.applying = false })
      },
    },
  }
</script>

<style scoped>
  .price-audit-skipped {
    padding: 9px 11px;
    border: 1px solid var(--app-color-warning-border);
    border-radius: 8px;
    background: var(--app-color-warning-soft);
  }
  .price-audit-skipped__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .price-audit-skipped__list {
    max-height: 170px;
    overflow-y: auto;
    font-size: 12px;
  }
  .price-audit-guidance {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-top: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text-secondary);
    font-size: 12px;
    line-height: 1.35;
  }
  .price-audit-guidance__item { display: inline-flex; align-items: flex-start; gap: 6px; }
  .price-audit-guidance__item > svg { flex: none; }
  .price-audit-selecting { display: inline-flex; align-items: center; gap: 7px; color: var(--app-color-primary); font-size: 12px; font-weight: 600; }
  .price-audit-product-name {
    color: var(--app-color-text);
    font-weight: 600;
  }
  .price-audit-product-code {
    color: var(--app-color-text-secondary);
    font-size: 11px;
    letter-spacing: .01em;
  }
  .price-audit-units {
    display: block;
    color: var(--app-color-text-secondary);
    font-size: 10px;
    line-height: 1.3;
  }
  .price-audit-price-cell__unit {
    color: var(--app-color-text-secondary);
    font-size: 10px;
  }
  .price-audit-list-badge {
    border: 1px solid var(--app-color-border);
    color: var(--app-color-text-secondary);
    font-weight: 600;
  }
  .price-audit-price-cell,
  .price-audit-utility-cell {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }
  .price-audit-price-cell__value {
    color: var(--app-color-primary);
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    letter-spacing: .01em;
  }
  .price-audit-utility-badge {
    min-width: 52px;
    padding: 4px 7px;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
  .price-audit-utility-cell__source {
    color: var(--app-color-text-secondary);
    font-size: 10px;
  }
  .price-audit-apply-info {
    display: flex;
    gap: 11px;
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid var(--app-color-primary-border);
    border-radius: 8px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-text-secondary);
  }
  .price-audit-apply-info__icon,
  .price-audit-apply-progress__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: var(--app-color-on-primary);
    background: var(--app-color-primary);
  }
  .price-audit-apply-info strong {
    display: block;
    margin-bottom: 2px;
    color: var(--app-color-text);
  }
  .price-audit-apply-info p {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
  }
  .price-audit-apply-info__note {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 7px;
    color: var(--app-color-primary);
    font-size: 11px;
    font-weight: 600;
  }
  .price-audit-bulk-notice {
    margin-bottom: 10px;
    padding: 8px 10px;
    border: 1px solid var(--app-color-border);
    border-radius: 6px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text-secondary);
    font-size: 12px;
  }
  .price-audit-bulk-notice--warning {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    border-color: #f0c36a;
    background: #fff8e8;
    color: #735215;
  }
  .price-audit-apply-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 300px;
    gap: 8px;
    text-align: center;
    color: var(--app-color-text-secondary);
  }
  .price-audit-apply-progress__icon {
    width: 42px;
    height: 42px;
    background: var(--app-color-primary-soft);
  }
  .price-audit-apply-progress strong {
    color: var(--app-color-text);
  }
  .price-audit-apply-progress span:last-child {
    font-size: 13px;
  }
  .price-audit-select-bar {
    justify-content: space-between;
    gap: 12px;
  }
  .price-audit-row-select {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 0;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: var(--app-color-primary);
    cursor: pointer;
    line-height: 1;
  }
  .price-audit-row-select__label {
    display: none;
  }
  .price-audit-row-select--selected {
    color: var(--app-color-text);
  }
  .price-audit-row-select:focus-visible {
    outline: 2px solid var(--app-color-warning);
    outline-offset: 2px;
  }
  .price-audit-selection-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .price-audit-bulk-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .price-audit-bulk-actions__count {
    font-size: 12px;
    color: var(--app-color-text-secondary);
    margin-right: 4px;
  }
  @media (max-width: 575.98px) {
    .price-audit-price-cell,
    .price-audit-utility-cell {
      align-items: flex-start;
    }
    .price-audit-apply-info {
      align-items: flex-start;
    }
  }
  @media (max-width: 767.98px) {
    .price-audit-row-select {
      min-height: 30px;
      padding: 6px 9px;
      border: 1px solid var(--app-color-warning-border);
      background: var(--app-color-warning-soft);
      color: var(--app-color-warning-hover);
      font-size: 12px;
      font-weight: 600;
    }
    .price-audit-row-select--selected {
      border-color: var(--app-color-warning);
      background: var(--app-color-warning);
      color: #fff;
    }
    .price-audit-row-select__label {
      display: inline;
    }
  }
</style>
